/**
 * Telegram Mini App SDK integration
 */

import {
  init,
  postEvent,
  backButton,
  initData,
  miniApp,
  viewport,
  themeParams,
} from '@telegram-apps/sdk-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

let sdkInitialized = false;

// Check if we're on mobile
export function isMobile(): boolean {
  const webApp = window.Telegram?.WebApp;
  if (!webApp) return false;
  const platform = (webApp as any).platform || '';
  return ['ios', 'android', 'android_x'].includes(platform.toLowerCase());
}

// Check if fullscreen
export function isFullscreen(): boolean {
  const webApp = window.Telegram?.WebApp;
  return !!(webApp as any)?.isFullscreen;
}

// Hook to reactively track fullscreen state
export function useFullscreen(): boolean {
  const [isFs, setIsFs] = useState(() => isFullscreen());

  useEffect(() => {
    const webApp = window.Telegram?.WebApp;
    if (!webApp) return;

    // Check immediately
    setIsFs(!!(webApp as any)?.isFullscreen);

    // Listen for fullscreen changes
    const handleFullscreenChanged = () => {
      setIsFs(!!(webApp as any)?.isFullscreen);
    };

    // Telegram WebApp emits events on the WebApp object
    if ((webApp as any).onEvent) {
      (webApp as any).onEvent('fullscreenChanged', handleFullscreenChanged);
      (webApp as any).onEvent('fullscreen_changed', handleFullscreenChanged);
      return () => {
        (webApp as any).offEvent('fullscreenChanged', handleFullscreenChanged);
        (webApp as any).offEvent('fullscreen_changed', handleFullscreenChanged);
      };
    }

    // Fallback: poll for changes
    const interval = setInterval(() => {
      const current = !!(webApp as any)?.isFullscreen;
      setIsFs(prev => prev !== current ? current : prev);
    }, 500);

    return () => clearInterval(interval);
  }, []);

  return isFs;
}

// Hook to get safe area insets (for proper header positioning)
// Mobile needs padding for Telegram's header bar, desktop/web doesn't
export function useSafeAreaInset(): { top: number; bottom: number } {
  const [insets, setInsets] = useState({ top: 0, bottom: 0 });

  useEffect(() => {
    const webApp = window.Telegram?.WebApp;
    if (!webApp) return;

    const updateInsets = () => {
      const platform = ((webApp as any).platform || '').toLowerCase();
      const isMobilePlatform = ['ios', 'android', 'android_x'].includes(platform);
      const isFs = !!(webApp as any)?.isFullscreen;
      
      // Desktop/web (platform = 'tdesktop', 'web', etc.) - no padding needed
      if (!isMobilePlatform) {
        setInsets({ top: 0, bottom: 0 });
        return;
      }
      
      // Telegram provides safeAreaInset and contentSafeAreaInset
      const safeArea = (webApp as any).safeAreaInset || { top: 0, bottom: 0 };
      const contentSafeArea = (webApp as any).contentSafeAreaInset || { top: 0, bottom: 0 };
      
      // Use the larger of the two
      let top = Math.max(safeArea.top || 0, contentSafeArea.top || 0);
      let bottom = Math.max(safeArea.bottom || 0, contentSafeArea.bottom || 0);
      
      // Mobile always needs safe area padding:
      // - Fullscreen: need notch + status bar space
      // - Non-fullscreen: Telegram's native header bar overlaps the webview
      if (isFs) {
        // Fullscreen: ensure minimum for notch
        if (top < 80) {
          top = platform === 'ios' ? Math.max(top, 100) : Math.max(top, 80);
        }
      } else {
        // Non-fullscreen: Telegram header takes ~56-60px, plus safe area
        // The webview starts at the TOP of screen, behind Telegram's header
        const tgHeaderHeight = 56;
        const statusBarHeight = platform === 'ios' ? 47 : 24;
        top = Math.max(top, tgHeaderHeight + statusBarHeight);
      }
      
      setInsets({ top, bottom });
    };

    updateInsets();

    // Listen for safe area changes
    if ((webApp as any).onEvent) {
      (webApp as any).onEvent('safeAreaChanged', updateInsets);
      (webApp as any).onEvent('contentSafeAreaChanged', updateInsets);
      (webApp as any).onEvent('fullscreenChanged', updateInsets);
      return () => {
        (webApp as any).offEvent('safeAreaChanged', updateInsets);
        (webApp as any).offEvent('contentSafeAreaChanged', updateInsets);
        (webApp as any).offEvent('fullscreenChanged', updateInsets);
      };
    }

    // Fallback: poll
    const interval = setInterval(updateInsets, 500);
    return () => clearInterval(interval);
  }, []);

  return insets;
}

// Request fullscreen (mobile only)
export function requestFullscreen(): void {
  const webApp = window.Telegram?.WebApp;
  if (webApp && (webApp as any).requestFullscreen) {
    try {
      (webApp as any).requestFullscreen();
    } catch (e) {
      console.warn('requestFullscreen failed:', e);
    }
  }
}

export function initTelegramSDK(): boolean {
  if (sdkInitialized) return true;

  try {
    init();
  } catch (e) {
    console.warn('SDK init() failed:', e);
    // Continue anyway - might still work with fallbacks
  }
  
  // Request fullscreen on mobile
  if (isMobile()) {
    requestFullscreen();
  }

  try {
    if (backButton.mount.isAvailable()) {
      backButton.mount();
    }
  } catch (e) {
    console.warn('backButton mount failed:', e);
  }

  try {
    if (viewport.mount.isAvailable()) {
      viewport.mount();
      if (viewport.expand.isAvailable()) {
        viewport.expand();
      }
    }
  } catch (e) {
    console.warn('viewport mount failed:', e);
  }

  try {
    if (miniApp.mount.isAvailable()) {
      miniApp.mount();
    }
  } catch (e) {
    console.warn('miniApp mount failed:', e);
  }

  try {
    if (themeParams.mount.isAvailable()) {
      themeParams.mount();
    }
  } catch (e) {
    console.warn('themeParams mount failed:', e);
  }

  try {
    postEvent('web_app_ready');
  } catch (e) {
    console.warn('web_app_ready failed:', e);
  }

  sdkInitialized = true;
  return true;
}

export function isInsideTelegram(): boolean {
  return typeof window !== 'undefined' && !!window.Telegram?.WebApp;
}

export function getInitDataRaw(): string | undefined {
  // Try SDK first
  try {
    const data = initData.raw();
    if (data) {
      console.log('[getInitDataRaw] Got from SDK, length:', data.length);
      return data;
    }
  } catch (e) {
    console.warn('[getInitDataRaw] SDK initData.raw() failed:', e);
  }
  
  // Fallback to raw WebApp API
  try {
    const data = window.Telegram?.WebApp?.initData;
    if (data) {
      console.log('[getInitDataRaw] Got from WebApp fallback, length:', data.length);
      return data;
    }
  } catch (e) {
    console.warn('[getInitDataRaw] WebApp fallback failed:', e);
  }
  
  console.warn('[getInitDataRaw] No initData available');
  return undefined;
}

export function useInitData() {
  const raw = useMemo(() => getInitDataRaw(), []);

  const state = useMemo(() => {
    try {
      return initData.state();
    } catch {
      return null;
    }
  }, []);

  return {
    raw,
    state,
    isAuthenticated: !!raw,
  };
}

export function useTelegramUser() {
  const { state } = useInitData();

  const user = useMemo(() => {
    if (state?.user) {
      return state.user;
    }
    return window.Telegram?.WebApp?.initDataUnsafe?.user ?? null;
  }, [state]);

  return user;
}

export function useStartParam(): string | undefined {
  return useMemo(() => {
    try {
      const state = initData.state();
      return state?.startParam;
    } catch (e) {
      console.warn('Failed to parse initData:', e);
      // Fallback to raw WebApp API
      return window.Telegram?.WebApp?.initDataUnsafe?.start_param;
    }
  }, []);
}

export function useBackButton(onBack?: () => void) {
  const [isVisible, setIsVisible] = useState(false);

  const show = useCallback(() => {
    try {
      if (backButton.show.isAvailable()) {
        backButton.show();
        setIsVisible(true);
      }
    } catch {}
  }, []);

  const hide = useCallback(() => {
    try {
      if (backButton.hide.isAvailable()) {
        backButton.hide();
        setIsVisible(false);
      }
    } catch {}
  }, []);

  useEffect(() => {
    if (!onBack) return;

    try {
      if (backButton.onClick.isAvailable()) {
        backButton.onClick(onBack);
        return () => {
          backButton.offClick(onBack);
        };
      }
    } catch {}
  }, [onBack]);

  return { show, hide, isVisible };
}

export function hapticFeedback(
  type: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft' = 'light'
) {
  try {
    postEvent('web_app_trigger_haptic_feedback', {
      type: 'impact',
      impact_style: type,
    });
  } catch {}
}

export function hapticNotification(type: 'success' | 'warning' | 'error') {
  try {
    postEvent('web_app_trigger_haptic_feedback', {
      type: 'notification',
      notification_type: type,
    });
  } catch {}
}

export function shareConfession(confessionId: string, text: string, agentName?: string) {
  try {
    // Create a shareable message with deep link
    const truncatedText = text.slice(0, 100) + (text.length > 100 ? '...' : '');
    const shareText = `🤫 AI Confession${agentName ? ` from ${agentName}` : ''}:\n\n"${truncatedText}"\n\nMore confessions:`;
    const deepLink = `https://t.me/clawdfessions_bot/app?startapp=c_${confessionId}`;
    
    postEvent('web_app_open_tg_link', {
      path_full: `/share/url?url=${encodeURIComponent(deepLink)}&text=${encodeURIComponent(shareText)}`,
    });
  } catch {}
}

/**
 * Share via inline query (fallback for when shareMessage doesn't work)
 * This opens inline mode with a pre-filled query that triggers the inline handler
 */
export function shareViaInlineQuery(confessionId: string): boolean {
  try {
    const webApp = window.Telegram?.WebApp as any;
    if (!webApp || typeof webApp.switchInlineQuery !== 'function') {
      console.log('[shareViaInlineQuery] switchInlineQuery not available');
      return false;
    }
    
    // switchInlineQuery with query that identifies the confession
    // The bot's inline handler will pick this up and show the confession
    console.log(`[shareViaInlineQuery] Opening inline mode for confession ${confessionId}`);
    webApp.switchInlineQuery(`share_${confessionId}`, ['users', 'groups', 'channels']);
    return true;
  } catch (e) {
    console.error('[shareViaInlineQuery] Error:', e);
    return false;
  }
}

export function openTelegramLink(url: string) {
  try {
    if (url.startsWith('https://t.me/')) {
      postEvent('web_app_open_tg_link', {
        path_full: url.replace('https://t.me', ''),
      });
    }
  } catch {}
}

/**
 * Check if native share is supported (requires WebApp 8.0+)
 */
export function isNativeShareSupported(): boolean {
  const webApp = window.Telegram?.WebApp;
  if (!webApp) return false;
  
  // shareMessage requires version 8.0+
  const version = parseFloat(webApp.version || '0');
  const hasMethod = typeof webApp.shareMessage === 'function';
  
  console.log('[isNativeShareSupported] version:', webApp.version, 'parsed:', version, 'hasMethod:', hasMethod);
  
  return version >= 8.0 && hasMethod;
}

// Track share state for debugging
let shareCallCount = 0;
let lastShareTime = 0;
let lastShareResult: string | null = null;

// Event handlers for share events (set up once)
let shareEventsInitialized = false;

function initShareEvents() {
  if (shareEventsInitialized) return;
  shareEventsInitialized = true;
  
  const webApp = window.Telegram?.WebApp;
  if (!webApp) return;
  
  // Listen for share events (Bot API 8.0+)
  try {
    if ((webApp as any).onEvent) {
      (webApp as any).onEvent('shareMessageSent', () => {
        console.log('[ShareEvent] shareMessageSent triggered');
      });
      (webApp as any).onEvent('shareMessageFailed', (error: any) => {
        console.log('[ShareEvent] shareMessageFailed triggered:', error);
      });
      console.log('[ShareEvents] Event listeners registered');
    }
  } catch (e) {
    console.warn('[ShareEvents] Failed to register event listeners:', e);
  }
}

/**
 * Share a prepared message using native Telegram share UI
 * This shows a preview of the message before sending
 * @param preparedMessageId - The ID returned from savePreparedInlineMessage
 * @returns Promise that resolves to 'sent' | 'cancelled' | 'failed' | 'unsupported'
 */
export function shareMessage(preparedMessageId: string): Promise<'sent' | 'cancelled' | 'failed' | 'unsupported'> {
  // Initialize event listeners on first call
  initShareEvents();
  
  // Track call count for debugging
  shareCallCount++;
  const callNum = shareCallCount;
  const timeSinceLastShare = Date.now() - lastShareTime;
  const callId = `${callNum}-${Date.now().toString(36)}`;
  
  console.log(`[shareMessage:${callId}] === START (call #${callNum}) ===`);
  console.log(`[shareMessage:${callId}] Time since last share: ${timeSinceLastShare}ms`);
  console.log(`[shareMessage:${callId}] Last share result: ${lastShareResult}`);
  
  // Update tracking
  lastShareTime = Date.now();
  
  return new Promise((resolve) => {
    let resolved = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const startTime = Date.now();
    
    const safeResolve = (result: 'sent' | 'cancelled' | 'failed' | 'unsupported') => {
      if (resolved) {
        console.log(`[shareMessage:${callId}] Already resolved, ignoring:`, result);
        return;
      }
      resolved = true;
      lastShareResult = result;
      if (timeoutId) clearTimeout(timeoutId);
      const duration = Date.now() - startTime;
      console.log(`[shareMessage:${callId}] === RESOLVED: ${result} after ${duration}ms ===`);
      resolve(result);
    };
    
    try {
      const webApp = window.Telegram?.WebApp;
      
      // Log key info
      console.log(`[shareMessage:${callId}] WebApp version: ${webApp?.version}`);
      console.log(`[shareMessage:${callId}] preparedMessageId: ${preparedMessageId?.slice(0, 40)}...`);
      console.log(`[shareMessage:${callId}] preparedMessageId length: ${preparedMessageId?.length}`);
      
      // Validate preparedMessageId
      if (!preparedMessageId || typeof preparedMessageId !== 'string' || preparedMessageId.length < 10) {
        console.error(`[shareMessage:${callId}] Invalid preparedMessageId!`);
        safeResolve('failed');
        return;
      }
      
      if (!isNativeShareSupported()) {
        console.warn(`[shareMessage:${callId}] Native share not supported`);
        safeResolve('unsupported');
        return;
      }
      
      // CRITICAL: On iOS, we need a longer delay between share calls to allow
      // Telegram's internal state to reset. Capybobo works because they don't
      // have this issue - we need to give more time.
      // Use 300ms minimum delay, more if the last share was very recent
      const minDelay = timeSinceLastShare < 1000 ? 300 : 50;
      
      const doShare = () => {
        console.log(`[shareMessage:${callId}] Calling native shareMessage NOW...`);
        
        try {
          webApp!.shareMessage(preparedMessageId, (sent: unknown) => {
            const callbackTime = Date.now() - startTime;
            console.log(`[shareMessage:${callId}] Callback received after ${callbackTime}ms, sent=${sent}`);
            
            if (sent) {
              safeResolve('sent');
            } else {
              // Check if this was too fast (dialog didn't open)
              if (callbackTime < 300) {
                console.warn(`[shareMessage:${callId}] Very fast callback (${callbackTime}ms) - dialog may not have opened`);
              }
              safeResolve('cancelled');
            }
          });
          
          console.log(`[shareMessage:${callId}] shareMessage() call completed, waiting for callback...`);
          
        } catch (shareError: any) {
          console.error(`[shareMessage:${callId}] shareMessage() threw:`, shareError?.message || shareError);
          // The dialog might still have opened, so treat as cancelled rather than failed
          safeResolve('cancelled');
          return;
        }
        
        // Timeout fallback - 15 seconds to give user time
        timeoutId = setTimeout(() => {
          console.log(`[shareMessage:${callId}] Timeout after 15s, resolving as cancelled`);
          safeResolve('cancelled');
        }, 15000);
      };
      
      console.log(`[shareMessage:${callId}] Adding ${minDelay}ms delay before share`);
      setTimeout(doShare, minDelay);
      
    } catch (error: any) {
      console.error(`[shareMessage:${callId}] Outer exception:`, error?.message || error);
      safeResolve('failed');
    }
  });
}
